import { Link } from '@tanstack/react-router';
import { ArrowUpRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { RelatedPersonVm } from '@/features/dashboard/bookingChanges/types/BillViewModel.ts';
import InitialsAvatar from '@/components/ui/InitialsAvatar.tsx';
import { ParticipantStatus } from '@/features/dashboard/participants/types/ParticipantStatus.ts';
import { getParticipantStatusLabel } from '@/features/dashboard/participants/types/ParticipantStatus.utils.ts';

interface RelatedPersonRowProps {
  entry: RelatedPersonVm;
  /** Pierwszy wiersz stoi tuż pod kreską nagłówka, więc nie nosi własnej. */
  isFirst: boolean;
}

/**
 * Wiersz obsady rezerwacji. Osoba oglądana teraz nosi pomarańczowy awatar
 * i nie jest odnośnikiem — nie ma dokąd z niej prowadzić.
 */
const RelatedPersonRow = ({ entry, isFirst }: RelatedPersonRowProps) => {
  const rowClass = cn(
    'flex items-center gap-[11px] py-[11px] text-inherit no-underline',
    !isFirst && 'border-t border-neutral-100',
  );

  const content = (
    <>
      <InitialsAvatar
        initials={entry.person.initials}
        tone={entry.isCurrent ? 'accent' : 'plain'}
        size="sm"
      />
      <span className="block min-w-0">
        <span
          className={cn(
            'block text-sm text-ink',
            entry.isCurrent ? 'font-bold' : 'font-semibold',
          )}
        >
          {entry.person.fullName}
        </span>
        <span className="mt-0.5 block text-[13px] text-neutral-500">
          {/* Rezygnacja dopisuje się do roli, a nie zastępuje jej odznaką:
              wiersz jest wąski, a kafel obok i tak nosi pełne oznaczenie. */}
          {[
            entry.person.roleLabel,
            entry.isCurrent && 'ta osoba',
            entry.withdrawn &&
              getParticipantStatusLabel(
                ParticipantStatus.WITHDRAWN,
              ).toLocaleLowerCase('pl'),
          ]
            .filter(Boolean)
            .join(' · ')}
        </span>
      </span>
      {!entry.isCurrent && (
        <ArrowUpRight
          aria-hidden
          className="ml-auto size-3.5 shrink-0 text-neutral-400"
        />
      )}
    </>
  );

  if (entry.isCurrent || !entry.link) {
    return <div className={rowClass}>{content}</div>;
  }

  // Cel odnośnika wskazuje rodzic (`link`), bo zależy od roli osoby;
  // nawigację robi router, więc wejście na sąsiednią osobę nie przeładowuje
  // aplikacji.
  return (
    <Link to={entry.link.to} params={entry.link.params} className={rowClass}>
      {content}
    </Link>
  );
};

export default RelatedPersonRow;
